import {
    memo,
    type PropsWithChildren,
    type SyntheticEvent
} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import type { Option } from "types";
import { getFormInputProps } from "util/forms";
import { getOptionName } from "util/option";
import JsonEditor from "lib/jsoneditor";
import { selectPaymentProducts, selectSetupActiveCompany } from "features/general/selectors";
import Tabs from "ui/atoms/Tabs";
import { Checkbox } from "ui/forms/formik-elements";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, type BankingSDKConnectorsRecipientsPivotProps } from "./types";

const BankingSDKConnectorsRecipientsPivot = ({
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isBootstrapDataLoading,
    bootstrapData,
    children,
    initialValues,
    ...restProps
}: PropsWithChildren<BankingSDKConnectorsRecipientsPivotProps>) => {
    const onJsonEditorChange = (formField: string) => (value: string | object) => {
        setFieldValue(formField, value, true);
        setTimeout(() => setFieldTouched(formField, true));
    };

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: Option) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: {
                        xs: 2,
                        sm: 3
                    },
                    alignItems: 'flex-start'
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2
                    }}
                >
                    <TextField
                        fullWidth
                        required
                        disabled
                        size='small'
                        name={FormField.BankId}
                        value={initialValues[FormField.BankId]}
                        label='Bank ID'
                    />
                    <TextField
                        fullWidth
                        required
                        disabled
                        size='small'
                        name={FormField.RecipientId}
                        value={initialValues[FormField.RecipientId]}
                        label='Recipient ID'
                    />
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        selectOptions={selectSetupActiveCompany}
                        size='small'
                        layout={null}
                        loading={isBootstrapDataLoading}
                        disabled={isBootstrapDataLoading}
                        value={values[FormField.Mor] || null}
                        onChange={onAutocompleteChange(FormField.Mor)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Mor, restProps),
                            name: FormField.Mor,
                            label: 'Domain'
                        }}
                    />
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        selectOptions={selectPaymentProducts}
                        size='small'
                        layout={null}
                        loading={isBootstrapDataLoading}
                        disabled={isBootstrapDataLoading}
                        getOptionLabel={getOptionName}
                        value={values[FormField.PaymentProduct]}
                        onChange={onAutocompleteChange(FormField.PaymentProduct)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.PaymentProduct, restProps),
                            name: FormField.PaymentProduct,
                            label: 'Payment Product'
                        }}
                    />
                    <Checkbox
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        label='Enabled'
                        handleBlur={handleBlur}
                        CheckboxProps={{
                            inputProps: {
                                'aria-label': 'Enabled'
                            },
                            name: FormField.IsEnabled,
                            checked: values[FormField.IsEnabled]
                        }}
                    />
                </Box>
                <Tabs
                    model={[
                        {
                            label: 'Reg Reporting Codes',
                            component: (
                                <JsonEditor
                                    value={values[FormField.RegReportingCodes]}
                                    onChange={onJsonEditorChange(FormField.RegReportingCodes)}
                                />
                            )
                        }
                    ]}
                    TabListProps={{
                        'aria-label': 'Banking SDK Connectors Recipients Pivot Tabs',
                        variant: 'scrollable',
                        scrollButtons: 'auto'
                    }}
                />
            </Box>
            {children}
        </Box>
    );
};

export default memo(BankingSDKConnectorsRecipientsPivot);
